import { Space } from "antd";
import { FC, useEffect, useState } from "react";
import { useLoading } from "../../context/LoadingProvider/useLoading";
import { ActionButtonEnum } from "../../enums/actionButtonEnum";
import useActionButtons from "../../hooks/useActionButtons";
import { Output } from "../../services/types";
import { isString } from "../../utils/stringUtils";
import Button from "../UI/Button";
import RangePicker, {
  RangePickerValue,
  RangePickerValues,
} from "../UI/RangePicker";
import DefaultFormParserLayout from "./default-form-parser-layout";
import dayjs from "dayjs";
import {notificationError} from "../../services/notification";

export type RangeDatePickerAndActionsLayoutProps = {
  actions: ActionButtonEnum[];
  onRefresh: (
    dateIntervalStart: RangePickerValue,
    dateIntervalEnd: RangePickerValue,
    output: Output
  ) => Promise<string>;
  maxDays: number;
};

const RangeDatePickerAndActionsLayout: FC<
  RangeDatePickerAndActionsLayoutProps
> = ({ actions, onRefresh, maxDays }) => {
  const loading = useLoading();
  const TODAY = dayjs();
  const LAST_WEEK = dayjs().subtract(1, "week");

  const [report, setReport] = useState<string>("");
  const [rangeDate, setRangeDate] = useState<RangePickerValues>([
    LAST_WEEK,
    TODAY
  ]);

  const {
    isToShowRefreshAction,
    isToShowSaveCSVAction,
    isToShowSavePDFAction,
  } = useActionButtons(actions);

  const handleEnter = (event: KeyboardEvent) => {
    if (loading.loading) return;
    if (!event.repeat && event.key === 'Enter' && isToShowRefreshAction) {
      refreshReport()
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleEnter)
    return () => document.removeEventListener("keydown", handleEnter)
  })

  const refreshReport = async (output: Output = Output.HTML) => {
    loading.addLoading();

    const dateIntervalStart = rangeDate[0];
    const dateIntervalEnd = rangeDate[1];

    if (maxDays){
      if (dayjs(dateIntervalEnd).diff(dayjs(dateIntervalStart), 'days') > maxDays) {
        loading.removeLoading()
        notificationError(`Date range cannot exceed ${maxDays} days.  If multiple months are needed, please run individual months separately.`)
        return
      }

    }

    const res = await onRefresh(dateIntervalStart,dateIntervalEnd, output);

    if (Output.isHTML(output) && isString(res)) {
      setReport(res);
    }

    loading.removeLoading();
  };

  return (
    <DefaultFormParserLayout html={report}>
      <Space direction="horizontal" size="middle" wrap align="center">
        <RangePicker
          defaultValue={[LAST_WEEK, TODAY]}
          onChange={(e: RangePickerValues) => setRangeDate(e)}
          allowClear={false}
          format="MM-DD-YYYY"
        />

        {isToShowRefreshAction && (
          <Button onClick={() => refreshReport()} type="primary">
            Refresh
          </Button>
        )}

        {isToShowSaveCSVAction && (
          <Button type="primary" onClick={() => refreshReport(Output.CSV)}>
            Save as CSV
          </Button>
        )}

        {isToShowSavePDFAction && (
          <Button type="primary" onClick={() => refreshReport(Output.PDF)}>
            Save as PDF
          </Button>
        )}
      </Space>
    </DefaultFormParserLayout>
  );
};

export default RangeDatePickerAndActionsLayout;
